import React, { useState } from 'react';
import { useData } from '../data/DataProvider';
import { pbEngineer, protectedFileUrl } from '../lib/pb';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

export default function EngineerFiles() {
  const { data, ready } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState(null);

  const docs = (data.engineerDocs || []).filter((doc) => doc.status === 'active' || doc.status === '');
  const filtered = docs.filter((doc) => {
    const term = searchTerm.trim().toLowerCase();
    return !term ||
      doc.title.toLowerCase().includes(term) ||
      (doc.description || '').toLowerCase().includes(term) ||
      (doc.category || '').toLowerCase().includes(term) ||
      (doc.tags || []).some((t) => String(t).toLowerCase().includes(term));
  });

  const handleOpen = async (doc) => {
    if (!doc.file) {
      setError('This document has no file attached');
      return;
    }
    setOpeningId(doc.id);
    setError(null);
    try {
      // Fresh token at click time (pb.js cache is ~90s; never bake URLs).
      const url = await protectedFileUrl(
        { id: doc.id, collectionName: 'engineer_docs' },
        doc.file,
      );
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error('Failed to open document:', err);
      setError('Could not open this document. Please try again.');
    } finally {
      setOpeningId(null);
    }
  };

  if (!ready) return <div className="page-container"><Skeleton rows={6} /></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>File System</h1>
        <p>Manuals, policies and office documents</p>
      </div>

      <div className="filter-bar">
        <div className="search-input">
          <SafeIcon icon={Icons.Search} size={16} />
          <input
            type="text"
            placeholder="Search documents…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search documents"
          />
        </div>
      </div>

      {error && <p className="form-hint" style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</p>}

      {filtered.length === 0 ? (
        <EmptyState
          title="No documents found"
          message={searchTerm ? `Nothing matches "${searchTerm}"` : 'No documents have been shared yet'}
          icon={Icons.FileText}
        />
      ) : (
        <div className="docs-grid">
          {filtered.map((doc) => (
            <div
              key={doc.id}
              className="doc-card"
              role="button"
              tabIndex={0}
              onClick={() => handleOpen(doc)}
              onKeyDown={(e) => e.key === 'Enter' && handleOpen(doc)}
            >
              <div className="doc-icon">
                <SafeIcon icon={doc.document_type === 'manual' ? Icons.Book : Icons.FileText} />
              </div>
              <h3>{doc.title}</h3>
              {doc.description && <p>{doc.description}</p>}
              <div className="doc-meta">
                <span>{doc.category || doc.document_type || 'document'}</span>
                {doc.version && <span>v{doc.version}</span>}
                <SafeIcon icon={openingId === doc.id ? Icons.Clock : Icons.Download} size={12} style={{ marginLeft: 'auto' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
